import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "@/lib/icons";
import { resolvePhotoUrl } from "@/lib/api";

/**
 * Full-width home banner slider.
 *  - Auto-advances every 5.5 s (pauses on hover)
 *  - Manual arrows + dot navigation
 *  - Ken Burns zoom on the active slide + scroll parallax
 *  - Height scales fluidly with viewport width, capped so very wide desktop
 *    screens still get a tall, fully-visible banner instead of a thin strip.
 */
export default function BannerSlider({ banners = [] }) {
  const slides = (banners || [])
    .filter((b) => b && b.enabled !== false && b.image_url)
    .slice(0, 5);
  const [i, setI] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [parallax, setParallax] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (slides.length <= 1 || hovering) return;
    const t = setInterval(
      () => setI((x) => (x + 1) % slides.length),
      5500,
    );
    return () => clearInterval(t);
  }, [slides.length, hovering]);

  useEffect(() => {
    const onScroll = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      // As user scrolls, banner image drifts down slower than page → parallax feel
      const y = Math.max(-80, Math.min(80, -rect.top * 0.22));
      setParallax(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (slides.length === 0) return null;

  const prev = () => setI((x) => (x - 1 + slides.length) % slides.length);
  const next = () => setI((x) => (x + 1) % slides.length);

  return (
    <section
      ref={wrapRef}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="relative w-full overflow-hidden bg-ink-950"
      style={{ height: "clamp(220px, 34vw, 460px)" }}
      data-testid="home-banner-slider"
      aria-roledescription="carousel"
    >
      {slides.map((b, idx) => {
        const active = idx === i;
        return (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity ease-out ${
              active ? "opacity-100 z-[1]" : "opacity-0 z-0"
            }`}
            style={{ transitionDuration: "1200ms" }}
            aria-hidden={!active}
          >
            {/* Parallax wrapper (moves on scroll) */}
            <div
              className="absolute inset-0 will-change-transform"
              style={{
                transform: `translate3d(0, ${parallax}px, 0)`,
                transition: "transform 200ms linear",
              }}
            >
              {/* Ken Burns wrapper (slow scale on active slide) */}
              <div className={`w-full h-full ${active ? "banner-kb" : ""}`}>
                <img
                  src={resolvePhotoUrl(b.image_url)}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
            </div>
            {/* Faint violet-magenta color grade + a light bottom vignette to
                ground the slide into the page — kept subtle on purpose so
                the photo itself stays the focus, not a black fade. */}
            <div className="banner-image-overlay absolute inset-0 bg-gradient-to-r from-violet-900/25 via-transparent to-magenta-900/20 mix-blend-multiply" />
            <div className="banner-image-overlay absolute inset-0 bg-gradient-to-b from-ink-950/10 via-transparent to-ink-950/40" />
          </div>
        );
      })}

      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            data-testid="banner-prev"
            aria-label="Previous banner"
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 h-9 w-9 md:h-10 md:w-10 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="banner-next"
            aria-label="Next banner"
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 h-9 w-9 md:h-10 md:w-10 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
            {slides.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setI(idx)}
                data-testid={`banner-dot-${idx}`}
                aria-label={`Go to banner ${idx + 1}`}
                aria-current={idx === i}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  idx === i
                    ? "w-8 bg-fuchsia-300"
                    : "w-1.5 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
